/**
 * ENG-5452 / FE6 — one row of the customer power user's org roll-up.
 *
 * Same table shape as the staff feed (`StaffFeedRow`), because both
 * are a power user scanning many engagements at once, and the two surfaces
 * sitting under the same tab strip would be jarring otherwise. What differs is
 * the audience: no red flags (those are staff triage), and a "needs your
 * action" chip instead, which is the whole reason a customer opens this list.
 *
 * The project name is the only click target, as on the staff feed. The roll-up
 * grants VIEW and nothing else (the api is explicit that the power user's own
 * action set is unchanged and lives on the Mine tab), so a row here must not
 * grow a second copy of the act-on-it affordances.
 */
import { Link } from 'react-router';

import Avatar from '@/auth/components/Avatar';

import { formatDateShort } from '../components/EngagementHub/format';
import type { ViewerRole } from '../components/EngagementHub/roles';
import StatusBadge from './StatusBadge';
import type { AccountRollupRow as Row } from './accountRollupViewModel';
import { nextActionCopyFor } from './nextActionCopy';

const CELL = 'px-12 py-12 align-top';

interface AccountRollupRowProps {
  row: Row;
}

export default function AccountRollupRow({ row }: AccountRollupRowProps): JSX.Element {
  return (
    <tr className="border-b border-surface-border transition-colors last:border-none focus-within:bg-surface-secondary hover:bg-surface-secondary">
      <td className={CELL}>
        <div className="flex min-w-[180px] flex-col gap-2">
          <Link
            to={`/engagements/${encodeURIComponent(row.engagementId)}`}
            className="text-left font-medium text-on-surface body-2"
          >
            {row.projectName}
          </Link>
          <span className="text-on-surface-muted util-caption">
            Opened {formatDateShort(row.createdAtIso)}
          </span>
        </div>
      </td>
      <td className={CELL}>
        <div className="flex items-center gap-8">
          {row.expertHasProfile ? (
            <Avatar fullName={row.expertName} sx={{ width: 28, height: 28, fontSize: 12 }} />
          ) : (
            <div
              className="flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-full border border-dashed border-surface-border bg-surface-elevated text-on-surface-muted util-caption"
              aria-hidden
            >
              ?
            </div>
          )}
          <span className="whitespace-nowrap text-on-surface body-2">{row.expertName}</span>
        </div>
      </td>
      <td className={CELL}>
        <StatusBadge status={row.status} awaiting={row.awaiting} viewerRole={badgeRoleFor(row)} />
      </td>
      <td className={CELL}>
        <NteCell row={row} />
      </td>
      <td className={CELL}>
        <ActionCell row={row} />
      </td>
    </tr>
  );
}

/**
 * Which copy branch the status badge should read.
 *
 * The badge's copy asks exactly one question at `pending_confirmation`: is the
 * viewer the one being waited on? `hasPendingAction` is the server's answer to
 * that, so it is the input, not the viewer's role.
 *
 * On a roll-up row the answer is usually no. This list reaches every engagement
 * billed to the account, including ones the viewer holds no role on, and the
 * fall-through copy there is "Awaiting your accept" — wrong, and a call to
 * action on a surface that offers none. That is the same defect ENG-5684 fixed
 * for the staff feed, and `of_fulfillment` is the copy table's existing branch
 * for "reads this from outside the set of people being waited on"
 * (`status.ts`). Reusing that branch beats adding a fourth role to every
 * exhaustive map in `roles.ts` for the sake of one string.
 */
function badgeRoleFor(row: Row): ViewerRole {
  return row.hasPendingAction ? 'unknown' : 'of_fulfillment';
}

function NteCell({ row }: { row: Row }) {
  if (!row.hasOffer) {
    return <span className="text-on-surface-muted body-2">—</span>;
  }
  return (
    <span className="whitespace-nowrap tabular-nums text-on-surface body-2">
      {row.nteConsumed ?? 0}h / {row.nteTotal}h
    </span>
  );
}

/**
 * The "needs my action" chip.
 *
 * Sourced from `hasPendingAction`, which the api derives with the same rule as
 * `myEngagementBuckets.actionNeeded` rather than a second copy of it, so a row
 * chipped here is a row that appears in the Mine tab's Action needed bucket.
 *
 * It is a chip and not a sort, because the api has no needs-my-action ordering
 * to offer: that ordering is not SQL-expressible over the grant joins, and
 * client-side sorting would reorder only the page in hand.
 */
function ActionCell({ row }: { row: Row }) {
  if (!row.hasPendingAction) {
    return <span className="text-on-surface-muted body-2">—</span>;
  }
  return (
    <span className="inline-flex items-center whitespace-nowrap rounded-full border border-brand-primary-faint bg-brand-light px-12 py-4 font-medium text-on-surface util-caption">
      {nextActionCopyFor(row.nextAction) ?? 'Needs your action'}
    </span>
  );
}
